import { useState, useEffect, useContext, createContext } from 'react';
import { App } from 'antd';
import { login as apiLogin, getUserInfo as apiGetUserInfo, logout as apiLogout } from '../api';
import { USER_INFO_STORAGE_KEY } from '../utils/constants';
// 创建认证上下文
const AuthContext = createContext();

// 兼容处理：接口返回的用户 info 字段可能是 JSON 字符串（如 "{\"company\":\"红象\"}"），
// 统一转换为 JS 对象；已是对象则原样返回
const normalizeUserInfo = (data) => {
    if (!data || typeof data !== 'object') return data;
    const normalized = { ...data };
    if (typeof normalized.info === 'string') {
        try {
            normalized.info = JSON.parse(normalized.info);
        } catch (error) {
            console.error('解析用户 info 字段失败:', error);
        }
    }
    return normalized;
};

// 认证状态管理Hook
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// 认证提供者组件
export const AuthProvider = ({ children }) => {
    const { message } = App.useApp();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    // 保存用户信息到本地存储
    const saveUserInfoToStorage = (userData) => {
        try {
            localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(userData));
        } catch (error) {
            console.error('保存用户信息到本地存储失败:', error);
        }
    };

    // 从本地存储加载用户信息
    const loadUserInfoFromStorage = () => {
        try {
            const storedUserInfo = localStorage.getItem(USER_INFO_STORAGE_KEY);
            if (storedUserInfo) {
                return JSON.parse(storedUserInfo);
            }
        } catch (error) {
            console.error('从本地存储加载用户信息失败:', error);
        }
        return null;
    };

    // 清除本地存储的用户信息
    const clearUserInfoFromStorage = () => {
        try {
            localStorage.removeItem(USER_INFO_STORAGE_KEY);
        } catch (error) {
            console.error('清除本地存储的用户信息失败:', error);
        }
    };

    // 获取用户信息
    const fetchUserInfo = async (token) => {
        try {
            const result = await apiGetUserInfo(token);
            if (result.errorCode === 0) {
                // 兼容后端返回：info 字段可能是 JSON 字符串，统一转换为对象
                const userData = normalizeUserInfo(result.data);
                // 保存用户信息到本地存储
                saveUserInfoToStorage(userData);
                setUserInfo(userData);
                return userData;
            } else if (result.errorCode === 20003) {
                // token失效，清除所有认证信息
                clearUserInfoFromStorage();
                localStorage.removeItem('accessToken');
                setIsAuthenticated(false);
                setUserInfo(null);
                message.error('登录已过期，请重新登录');
                throw new Error('登录已过期，请重新登录');
            } else {
                const errMsg = result.errorMsg || '获取用户信息失败';
                message.error(errMsg);
                throw new Error(errMsg);
            }
        } catch (error) {
            // 清除用户数据
            clearUserInfoFromStorage();
            localStorage.removeItem('accessToken');
            setIsAuthenticated(false);
            setUserInfo(null);

            const pathname = window.location.pathname;
            // /user 路径下：跳转到登录页
            if (pathname.startsWith('/user')) {
                window.location.replace('/login');
            }
            // 根路径下：只清除用户数据，不跳转

            console.error('获取用户信息失败:', error);
            throw error;
        }
    };

    // 初始化认证状态
    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('accessToken');
            if (token) {
                setIsAuthenticated(true);

                // 优先使用本地存储的用户信息
                const storedUserInfo = loadUserInfoFromStorage();
                if (storedUserInfo) {
                    // 兼容历史数据：本地存储中的 info 也可能是 JSON 字符串
                    setUserInfo(normalizeUserInfo(storedUserInfo));
                }

                // 异步获取最新的用户信息
                try {
                    await fetchUserInfo(token);
                } catch (error) {
                    // 如果获取用户信息失败，但本地有存储的用户信息，保持登录状态
                    if (!storedUserInfo) {
                        // 如果本地也没有用户信息，清除token
                        localStorage.removeItem('accessToken');
                        setIsAuthenticated(false);
                        clearUserInfoFromStorage();
                    }
                }
            }
            setLoading(false);
        };

        initializeAuth();
    }, []);

    // 登录方法
    const login = async (account, password) => {
        try {
            // 调用登录API验证用户名密码
            const result = await apiLogin(account, password);
            if (result.errorCode === 0) {
                const token = result.data.AccessToken;
                localStorage.setItem('accessToken', token);
                setIsAuthenticated(true);

                // 获取并保存用户信息
                await fetchUserInfo(token);
            } else {
                const errMsg = result.errorMsg || '登录失败';
                // message.error(errMsg);
                throw new Error(errMsg);
            }
        } catch (error) {
            // 如果登录失败，回滚状态
            localStorage.removeItem('accessToken');
            clearUserInfoFromStorage();
            setIsAuthenticated(false);
            throw error;
        }
    };

    // 登出方法
    const logout = async () => {
        try {
            await apiLogout();
        } catch (e) {
            console.error('登出接口调用失败:', e);
        }
        localStorage.removeItem('accessToken');
        clearUserInfoFromStorage();
        setIsAuthenticated(false);
        setUserInfo(null);
    };

    // 更新用户信息
    const updateUserInfo = (newUserInfo) => {
        setUserInfo(newUserInfo);
    };

    // 刷新用户信息（从网络重新获取）
    const refreshUserInfo = async () => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            try {
                await fetchUserInfo(token);
            } catch (error) {
                console.error('刷新用户信息失败:', error);
                throw error;
            }
        }
    };

    const value = {
        isAuthenticated,
        userInfo,
        loading,
        login,
        logout,
        updateUserInfo,
        refreshUserInfo
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};